import React, { createContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import socketManager from "../utils/SocketManager";

export const OnlineStatusContext = createContext();

const OnlineStatusProvider = ({ children }) => {
    const [onlineUsers, setOnlineUsers] = useState({});
    const { user } = useSelector((state) => state.user);

    const userId = user?.user?._id;

    useEffect(() => {
        if (!userId) return;

        // Optimistically mark ourselves online. The logged-in user is online
        // by definition — we shouldn't wait for the join → snapshot round-trip
        // before painting our own dot green. Before this line, fresh reloads
        // briefly showed self as offline (falling back to a stale
        // `member.lastActive` from the workspace fetch).
        setOnlineUsers((prev) => ({ ...prev, [userId]: true }));

        const handleUserStatus = (data) => {
            setOnlineUsers((prev) => ({
                ...prev,
                [data.userId]: data.online,
            }));
        };

        // Server emits this as the authoritative snapshot of who is currently
        // online (on join, and on reconnect). Replace the local map entirely
        // — merging would leave users who went offline between sessions or
        // across a network blip stuck at `true` forever.
        const handleOnlineUsersList = (userIds) => {
            const next = {};
            userIds.forEach((id) => {
                next[id] = true;
            });
            setOnlineUsers(next);
        };

        // Subscribe via the shared SocketManager connection. join + heartbeat
        // are handled centrally by SocketManager (no separate connection, no
        // duplicate 5s heartbeat).
        const unsubStatus = socketManager.on("user-online-status", handleUserStatus);
        const unsubList = socketManager.on("online-users-list", handleOnlineUsersList);

        return () => {
            unsubStatus();
            unsubList();
        };
    }, [userId]);

    return (
        <OnlineStatusContext.Provider value={onlineUsers}>
            {children}
        </OnlineStatusContext.Provider>
    );
};

export default OnlineStatusProvider;
